import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';

import { WINDOW, WebWorkerService, WindowEvent, WorkerType } from '@frontend/vanilla/core';
import { fromEvent, take } from 'rxjs';

import { DataLayerProxyService } from './data-layer-proxy.service';
import { PartytownService } from './partytown.service';
import { TrackingConfig } from './tracking.client-config';
import { ClientTagManager } from './tracking.models';

/**
 * @whatItDoes Injects all configured client-side tag manager scripts into the DOM.
 *
 * @description
 *
 * Client-side tag managers can be configured using TrackingConfig.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class TagManagerService {
    private readonly document = inject(DOCUMENT);
    private readonly window = inject(WINDOW);
    private readonly partytownService = inject(PartytownService);
    private readonly trackingConfig = inject(TrackingConfig);
    private readonly dataLayerProxy = inject(DataLayerProxyService);
    private readonly webWorkerService = inject(WebWorkerService);
    private readonly ngZone = inject(NgZone);

    /** @internal */
    init() {
        if (!this.trackingConfig.isEnabled) {
            return;
        }

        const configured = this.trackingConfig.clientTagManagers || [];
        const excludes = this.trackingConfig.clientInjectionExcludes || [];

        configured
            .filter((current: ClientTagManager) => !excludes.includes(current.name))
            .forEach((current: ClientTagManager) => this.injectScript(current));
    }

    /** @stable */
    get availableClientTagManagers(): string[] {
        return (this.trackingConfig.clientTagManagers || []).map((t: ClientTagManager) => t.name);
    }

    /**
     * Adds a tag manager init script for the given tag manager renderer name to the script unless present.
     * If the tag manager is already present in the DOM, it will return the existing element.
     *
     * @param rendererName - The name of the tag manager renderer to load.
     * @param type - The type of the script to load (default is 'text/javascript').
     * @return A promise that resolves to the script element that was injected or already exists in the DOM.
     * @throws Error if the tag manager with the given name does not exist in the configuration
     *
     * @stable
     */
    async load(rendererName: string, type: string = 'text/javascript'): Promise<Element> {
        const configured = this.trackingConfig.clientTagManagers || [];
        const tagManager = configured.find((current: ClientTagManager) => current.name === rendererName);

        if (!tagManager) {
            throw new Error(`Client TagManager "${rendererName}" does not exist.`);
        }

        const existing = this.document.querySelector('#' + tagManager.name);

        if (existing) {
            return existing;
        }

        const forceSchedulerDisabled = type === 'text/partytown';

        await this.dataLayerProxy.patchDataLayer(forceSchedulerDisabled);

        return this.injectScript(tagManager, type);
    }

    private injectScript(tagManager: ClientTagManager, type = 'text/javascript'): Promise<HTMLScriptElement> {
        const id = tagManager.name;
        const script = this.document.createElement('script');
        script.async = true;
        script.id = id;
        script.text = tagManager.script;
        script.type = type;

        const promise = new Promise<HTMLScriptElement>((resolve, reject) => {
            script.onload = () => resolve(script);
            script.onerror = () => reject(new Error(`Client TagManager "${id}" failed to load.`));
        });

        this.document.head.append(script);

        return promise;
    }

    public loadPartyTownGTM() {
        const isDisabledLocally = localStorage.getItem('partytown-disabled') === '1';
        const isServiceWorkerSupported = 'serviceWorker' in navigator;
        if (isServiceWorkerSupported && !isDisabledLocally && this.window.partytown && this.window._ptReady) {
            this.window._ptReady
                .then(() => {
                    this.window.dataLayer = this.window.dataLayer ?? [];
                    this.ngZone.runOutsideAngular(() => {
                        this.partytownService.feedSharedStorage();
                        this.partytownService.feedCookieStorage();
                        fromEvent(this.document, 'gtm_loaded')
                            .pipe(take(1))
                            .subscribe(() => {
                                // tell tracking system gtm was loaded
                                this.push({ event: WindowEvent.VanillaGtmLoaded });
                                this.push({ event: WindowEvent.GtmLoad });
                                if (this.window.partytown.enableEventReplay) {
                                    this.webWorkerService.createWorker(
                                        WorkerType.PartytownCreateEventReplayQueueTimeout,
                                        {
                                            timeout: 5000,
                                        },
                                        () => {
                                            this.partytownService.createEventReplayQueue();
                                            this.webWorkerService.removeWorker(WorkerType.PartytownCreateEventReplayQueueTimeout);
                                        },
                                    );
                                }
                            });

                        this.load('GoogleTagManagerRenderer', 'text/partytown');

                        const triggerPtUpdate = () => {
                            this.webWorkerService.createWorker(WorkerType.PartytownUpdateTimeout, { timeout: 0 }, () => {
                                this.window.dispatchEvent(new CustomEvent('ptupdate'));
                                this.webWorkerService.removeWorker(WorkerType.PartytownUpdateTimeout);
                            });
                        };

                        if (this.document.readyState === 'complete') {
                            triggerPtUpdate();
                        } else {
                            this.window.addEventListener('load', triggerPtUpdate);
                        }
                    });
                })
                .catch(() => {
                    this.load('GoogleTagManagerRenderer');
                });
        } else {
            this.load('GoogleTagManagerRenderer');
        }
    }

    private push(payload: object) {
        if (this.window.dataLayer) {
            const dataLayer = this.window.dataLayer;
            // workaround to don't trigger dataLayer.push
            // since it is patched by partytown and unnecessarily propagates data to the worker
            dataLayer[dataLayer.length] = payload;
        }
    }
}
